import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import eq from "lodash-es/eq"
import map from "lodash-es/map"

import { CheckMarkIcon } from "@/3514/assets"
import {
    ESpecialChar,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    SpecialChars,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { THEME } from "@/3514/theme"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { participantHoldStyles as styles } from "./participant.hold.styles"
import {
    EParticipantHoldsMode as EMode,
    type TParticipantHoldProps as TProps,
    type TParticipantHoldSession as TSession
} from "./participant.hold.types"
import { participantHoldUtils as utils } from "./participant.hold.utils"

const { renderSessionDate } = utils
const { hold: classes } = styles

const ParticipantHold: FC<TProps> = ({
    mode,
    setIndex,
    isSelected,
    firstSessionGetter,
    restSessionsGetter,
    sessions
}: TProps): ReactElement => {
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const { t }: IUseTranslation = useTranslation()

    const mainSession: TSession = firstSessionGetter(sessions, setIndex)
    const sessionsSet: TSession[] = restSessionsGetter(sessions, setIndex)

    const isSelectable: boolean = eq(mode, EMode.Selectable)

    return (
        <article
            className={classes.container({ mode, isSelected })}
            {...(isSelectable && { role: "tab", tabIndex: 0 })}
        >
            {isSelectable && (
                <CheckMarkIcon className={classes.mark({ isSelected })} fillColor={THEME.COLORS.gray[100]} />
            )}

            <div className={classes.col()}>
                <Typography
                    text={
                        breakpoints.xs || breakpoints.sm ? (
                            <Fragment>
                                {t("entities.holds.mainSession.title")}
                                {SpecialChars[ESpecialChar.Colon]}
                            </Fragment>
                        ) : (
                            t("entities.holds.mainSession.title")
                        )
                    }
                    className={classes.colTitle()}
                    color={isSelected ? ETypographyColor.Dark : ETypographyColor.Black}
                    tag={ETypographyTag.Heading6}
                    size={ETypographySize.Small}
                    weight={ETypographyWeight.Bold}
                />

                <div className={classes.mainSessionContainer({ isSelected })}>
                    {renderSessionDate({
                        isMain: true,
                        isSelected,
                        startDate: mainSession.startDate,
                        endDate: mainSession.endDate,
                        t
                    })}
                </div>
            </div>

            <div className={classes.col()}>
                <Typography
                    text={
                        <Fragment>
                            {t("entities.holds.restSessions.title")}
                            {SpecialChars[ESpecialChar.Colon]}
                        </Fragment>
                    }
                    className={classes.colTitle()}
                    color={isSelected ? ETypographyColor.Dark : ETypographyColor.Black}
                    tag={ETypographyTag.Heading6}
                    size={ETypographySize.Small}
                    weight={ETypographyWeight.Bold}
                />

                <ul className={classes.sessionSetContainer()}>
                    {map<TSession, ReactElement>(
                        sessionsSet,
                        (s: TSession, idx: number): ReactElement => (
                            <li key={`${s.startDate}-${idx}`}>
                                {renderSessionDate({
                                    isMain: false,
                                    isSelected,
                                    startDate: s.startDate,
                                    endDate: s.endDate,
                                    t
                                })}
                            </li>
                        )
                    )}
                </ul>
            </div>
        </article>
    )
}

ParticipantHold.displayName = "ParticipantHold"

export { ParticipantHold }
